import React from 'react';

const FormDetailsInputs = ({ currentStep, formData, updateFormData }) => {
  const handlePersonalChange = (field, value) => {
    updateFormData('personalDetails', { [field]: value });
  };

  const handleContactChange = (field, value) => {
    updateFormData('contactDetails', { [field]: value });
  };

  const handleAddressChange = (type, field, value) => {
    updateFormData('contactDetails', {
      [type]: { ...formData.contactDetails[type], [field]: value }
    });
  };

  const handleSameAsPermanent = (checked) => {
    if (checked) {
      updateFormData('contactDetails', {
        sameAsPermanent: true,
        currentAddress: { ...formData.contactDetails.permanentAddress }
      });
    } else {
      updateFormData('contactDetails', { sameAsPermanent: false });
    }
  };

  const handleAcademicChange = (level, field, value) => {
    updateFormData('academicDetails', {
      [level]: { ...formData.academicDetails[level], [field]: value }
    });
  };

  // Helper class for consistent input styling
  const inputClassName = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 outline-none";
  const labelClassName = "block text-sm font-medium text-gray-700 mb-2";

  // Step 1: Personal Details
  if (currentStep === 1) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2 border-b pb-4">
          👤 Personal Details
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div className="sm:col-span-2">
            <label className={labelClassName}>
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.personalDetails.fullName}
              onChange={(e) => handlePersonalChange('fullName', e.target.value)}
              className={inputClassName}
              placeholder="Enter your full name"
              required
            />
          </div>

          <div>
            <label className={labelClassName}>
              Father's Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.personalDetails.fatherName}
              onChange={(e) => handlePersonalChange('fatherName', e.target.value)}
              className={inputClassName}
              placeholder="Father's name"
              required
            />
          </div>

          <div>
            <label className={labelClassName}>
              Mother's Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.personalDetails.motherName}
              onChange={(e) => handlePersonalChange('motherName', e.target.value)}
              className={inputClassName}
              placeholder="Mother's name"
              required
            />
          </div>

          <div>
            <label className={labelClassName}>
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.personalDetails.dateOfBirth}
              onChange={(e) => handlePersonalChange('dateOfBirth', e.target.value)}
              className={inputClassName}
              required
            />
          </div>

          <div>
            <label className={labelClassName}>
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.personalDetails.gender}
              onChange={(e) => handlePersonalChange('gender', e.target.value)}
              className={inputClassName}
              required
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className={labelClassName}>Blood Group</label>
            <select
              value={formData.personalDetails.bloodGroup}
              onChange={(e) => handlePersonalChange('bloodGroup', e.target.value)}
              className={inputClassName}
            >
              <option value="">Select Blood Group</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
          </div>

          <div>
            <label className={labelClassName}>
              Nationality <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.personalDetails.nationality}
              onChange={(e) => handlePersonalChange('nationality', e.target.value)}
              className={inputClassName}
              required
            />
          </div>

          <div>
            <label className={labelClassName}>Religion</label>
            <input
              type="text"
              value={formData.personalDetails.religion}
              onChange={(e) => handlePersonalChange('religion', e.target.value)}
              className={inputClassName}
              placeholder="Your religion"
            />
          </div>

          <div>
            <label className={labelClassName}>
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.personalDetails.category}
              onChange={(e) => handlePersonalChange('category', e.target.value)}
              className={inputClassName}
              required
            >
              <option value="">Select Category</option>
              <option value="General">General</option>
              <option value="OBC">OBC</option>
              <option value="SC">SC</option>
              <option value="ST">ST</option>
              <option value="EWS">EWS</option>
            </select>
          </div>

          <div>
            <label className={labelClassName}>
              Aadhar Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.personalDetails.aadharNumber}
              onChange={(e) => handlePersonalChange('aadharNumber', e.target.value)}
              className={inputClassName}
              placeholder="XXXX-XXXX-XXXX"
              maxLength="12"
              required
            />
          </div>

          <div>
            <label className={labelClassName}>PAN Number</label>
            <input
              type="text"
              value={formData.personalDetails.panNumber}
              onChange={(e) => handlePersonalChange('panNumber', e.target.value)}
              className={inputClassName}
              placeholder="ABCDE1234F"
              maxLength="10"
            />
          </div>

          <div>
            <label className={labelClassName}>Marital Status</label>
            <select
              value={formData.personalDetails.maritalStatus}
              onChange={(e) => handlePersonalChange('maritalStatus', e.target.value)}
              className={inputClassName}
            >
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Divorced">Divorced</option>
              <option value="Widowed">Widowed</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.personalDetails.physicallyChallenged}
                onChange={(e) => handlePersonalChange('physicallyChallenged', e.target.checked)}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Physically Challenged
              </span>
            </label>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Contact & Address
  if (currentStep === 2) {
    return (
      <div className="space-y-8 animate-fadeIn">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2 border-b pb-4">
          📍 Contact & Address Details
        </h2>

        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className={labelClassName}>
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.contactDetails.email}
                onChange={(e) => handleContactChange('email', e.target.value)}
                className={inputClassName}
                placeholder="your@email.com"
                required
              />
            </div>

            <div>
              <label className={labelClassName}>
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.contactDetails.mobile}
                onChange={(e) => handleContactChange('mobile', e.target.value)}
                className={inputClassName}
                placeholder="+91 XXXXX XXXXX"
                maxLength="10"
                required
              />
            </div>

            <div>
              <label className={labelClassName}>Alternate Mobile</label>
              <input
                type="tel"
                value={formData.contactDetails.alternateMobile}
                onChange={(e) => handleContactChange('alternateMobile', e.target.value)}
                className={inputClassName}
                placeholder="+91 XXXXX XXXXX"
                maxLength="10"
              />
            </div>

            <div>
              <label className={labelClassName}>WhatsApp Number</label>
              <input
                type="tel"
                value={formData.contactDetails.whatsappNumber}
                onChange={(e) => handleContactChange('whatsappNumber', e.target.value)}
                className={inputClassName}
                placeholder="+91 XXXXX XXXXX"
                maxLength="10"
              />
            </div>
          </div>
        </div>

        {/* Permanent Address */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 border-t pt-4">Permanent Address</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="sm:col-span-2">
              <label className={labelClassName}>
                Address Line 1 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.contactDetails.permanentAddress.addressLine1}
                onChange={(e) => handleAddressChange('permanentAddress', 'addressLine1', e.target.value)}
                className={inputClassName}
                placeholder="House No., Street Name"
                required
              />
            </div>

            <div className="sm:col-span-2">
              <label className={labelClassName}>Address Line 2</label>
              <input
                type="text"
                value={formData.contactDetails.permanentAddress.addressLine2}
                onChange={(e) => handleAddressChange('permanentAddress', 'addressLine2', e.target.value)}
                className={inputClassName}
                placeholder="Landmark, Area"
              />
            </div>

            <div>
              <label className={labelClassName}>
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.contactDetails.permanentAddress.city}
                onChange={(e) => handleAddressChange('permanentAddress', 'city', e.target.value)}
                className={inputClassName}
                placeholder="City"
                required
              />
            </div>

            <div>
              <label className={labelClassName}>
                State <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.contactDetails.permanentAddress.state}
                onChange={(e) => handleAddressChange('permanentAddress', 'state', e.target.value)}
                className={inputClassName}
                placeholder="State"
                required
              />
            </div>

            <div>
              <label className={labelClassName}>
                PIN Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.contactDetails.permanentAddress.pinCode}
                onChange={(e) => handleAddressChange('permanentAddress', 'pinCode', e.target.value)}
                className={inputClassName}
                placeholder="PIN Code"
                maxLength="6"
                required
              />
            </div>

            <div>
              <label className={labelClassName}>Country</label>
              <input
                type="text"
                value={formData.contactDetails.permanentAddress.country}
                onChange={(e) => handleAddressChange('permanentAddress', 'country', e.target.value)}
                className={inputClassName}
                placeholder="Country"
              />
            </div>
          </div>
        </div>

        {/* Current Address */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b pb-2 gap-2 mt-6">
            <h3 className="text-lg font-semibold text-gray-800">Current Address</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.contactDetails.sameAsPermanent}
                onChange={(e) => handleSameAsPermanent(e.target.checked)}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Same as Permanent</span>
            </label>
          </div>

          {!formData.contactDetails.sameAsPermanent && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 animate-fadeIn">
              <div className="sm:col-span-2">
                <label className={labelClassName}>Address Line 1</label>
                <input
                  type="text"
                  value={formData.contactDetails.currentAddress.addressLine1}
                  onChange={(e) => handleAddressChange('currentAddress', 'addressLine1', e.target.value)}
                  className={inputClassName}
                  placeholder="House No., Street Name"
                />
              </div>

              <div className="sm:col-span-2">
                <label className={labelClassName}>Address Line 2</label>
                <input
                  type="text"
                  value={formData.contactDetails.currentAddress.addressLine2}
                  onChange={(e) => handleAddressChange('currentAddress', 'addressLine2', e.target.value)}
                  className={inputClassName}
                  placeholder="Landmark, Area"
                />
              </div>

              <div>
                <label className={labelClassName}>City</label>
                <input
                  type="text"
                  value={formData.contactDetails.currentAddress.city}
                  onChange={(e) => handleAddressChange('currentAddress', 'city', e.target.value)}
                  className={inputClassName}
                  placeholder="City"
                />
              </div>

              <div>
                <label className={labelClassName}>State</label>
                <input
                  type="text"
                  value={formData.contactDetails.currentAddress.state}
                  onChange={(e) => handleAddressChange('currentAddress', 'state', e.target.value)}
                  className={inputClassName}
                  placeholder="State"
                />
              </div>

              <div>
                <label className={labelClassName}>PIN Code</label>
                <input
                  type="text"
                  value={formData.contactDetails.currentAddress.pinCode}
                  onChange={(e) => handleAddressChange('currentAddress', 'pinCode', e.target.value)}
                  className={inputClassName}
                  placeholder="PIN Code"
                  maxLength="6"
                />
              </div>

              <div>
                <label className={labelClassName}>Country</label>
                <input
                  type="text"
                  value={formData.contactDetails.currentAddress.country}
                  onChange={(e) => handleAddressChange('currentAddress', 'country', e.target.value)}
                  className={inputClassName}
                  placeholder="Country"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Step 3: Academic Details
  if (currentStep === 3) {
    return (
      <div className="space-y-8 animate-fadeIn">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2 border-b pb-4">
          🎓 Academic Details
        </h2>

        {/* Class 10 */}
        <div className="space-y-4 p-4 sm:p-6 bg-blue-50 rounded-xl border border-blue-100 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 border-b border-blue-200 pb-2">
            Class 10th Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className={labelClassName}>Board</label>
              <input
                type="text"
                value={formData.academicDetails.class10.boardUniversity}
                onChange={(e) => handleAcademicChange('class10', 'boardUniversity', e.target.value)}
                className={inputClassName}
                placeholder="CBSE, State Board, etc."
              />
            </div>

            <div>
              <label className={labelClassName}>School Name</label>
              <input
                type="text"
                value={formData.academicDetails.class10.schoolCollege}
                onChange={(e) => handleAcademicChange('class10', 'schoolCollege', e.target.value)}
                className={inputClassName}
                placeholder="School name"
              />
            </div>

            <div>
              <label className={labelClassName}>Roll Number</label>
              <input
                type="text"
                value={formData.academicDetails.class10.rollNumber}
                onChange={(e) => handleAcademicChange('class10', 'rollNumber', e.target.value)}
                className={inputClassName}
                placeholder="Roll number"
              />
            </div>

            <div>
              <label className={labelClassName}>Passing Year</label>
              <input
                type="number"
                value={formData.academicDetails.class10.passingYear}
                onChange={(e) => handleAcademicChange('class10', 'passingYear', parseInt(e.target.value))}
                className={inputClassName}
                placeholder="2020"
              />
            </div>

            <div>
              <label className={labelClassName}>Percentage</label>
              <input
                type="number"
                step="0.01"
                value={formData.academicDetails.class10.percentage}
                onChange={(e) => handleAcademicChange('class10', 'percentage', parseFloat(e.target.value))}
                className={inputClassName}
                placeholder="85.5"
              />
            </div>

            <div>
              <label className={labelClassName}>CGPA</label>
              <input
                type="number"
                step="0.01"
                value={formData.academicDetails.class10.cgpa}
                onChange={(e) => handleAcademicChange('class10', 'cgpa', parseFloat(e.target.value))}
                className={inputClassName}
                placeholder="9.5"
              />
            </div>
          </div>
        </div>

        {/* Class 12 */}
        <div className="space-y-4 p-4 sm:p-6 bg-purple-50 rounded-xl border border-purple-100 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 border-b border-purple-200 pb-2">
            Class 12th Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className={labelClassName}>Board</label>
              <input
                type="text"
                value={formData.academicDetails.class12.boardUniversity}
                onChange={(e) => handleAcademicChange('class12', 'boardUniversity', e.target.value)}
                className={inputClassName}
                placeholder="CBSE, State Board, etc."
              />
            </div>

            <div>
              <label className={labelClassName}>School Name</label>
              <input
                type="text"
                value={formData.academicDetails.class12.schoolCollege}
                onChange={(e) => handleAcademicChange('class12', 'schoolCollege', e.target.value)}
                className={inputClassName}
                placeholder="School name"
              />
            </div>

            <div>
              <label className={labelClassName}>Roll Number</label>
              <input
                type="text"
                value={formData.academicDetails.class12.rollNumber}
                onChange={(e) => handleAcademicChange('class12', 'rollNumber', e.target.value)}
                className={inputClassName}
                placeholder="Roll number"
              />
            </div>

            <div>
              <label className={labelClassName}>Passing Year</label>
              <input
                type="number"
                value={formData.academicDetails.class12.passingYear}
                onChange={(e) => handleAcademicChange('class12', 'passingYear', parseInt(e.target.value))}
                className={inputClassName}
                placeholder="2022"
              />
            </div>

            <div>
              <label className={labelClassName}>Stream</label>
              <input
                type="text"
                value={formData.academicDetails.class12.stream}
                onChange={(e) => handleAcademicChange('class12', 'stream', e.target.value)}
                className={inputClassName}
                placeholder="Science, Commerce, Arts"
              />
            </div>

            <div>
              <label className={labelClassName}>Subjects</label>
              <input
                type="text"
                value={formData.academicDetails.class12.subjects}
                onChange={(e) => handleAcademicChange('class12', 'subjects', e.target.value)}
                className={inputClassName}
                placeholder="Physics, Chemistry, Maths"
              />
            </div>

            <div>
              <label className={labelClassName}>Percentage</label>
              <input
                type="number"
                step="0.01"
                value={formData.academicDetails.class12.percentage}
                onChange={(e) => handleAcademicChange('class12', 'percentage', parseFloat(e.target.value))}
                className={inputClassName}
                placeholder="85.5"
              />
            </div>

            <div>
              <label className={labelClassName}>CGPA</label>
              <input
                type="number"
                step="0.01"
                value={formData.academicDetails.class12.cgpa}
                onChange={(e) => handleAcademicChange('class12', 'cgpa', parseFloat(e.target.value))}
                className={inputClassName}
                placeholder="9.5"
              />
            </div>
          </div>
        </div>

        {/* Graduation Details */}
        <div className="space-y-4 p-4 sm:p-6 bg-green-50 rounded-xl border border-green-100 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 border-b border-green-200 pb-2">
            Graduation / Diploma Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className={labelClassName}>Degree / Course</label>
              <input
                type="text"
                value={formData.academicDetails.graduation?.degree || ''}
                onChange={(e) => handleAcademicChange('graduation', 'degree', e.target.value)}
                className={inputClassName}
                placeholder="B.Tech, B.Sc, BCA, etc."
              />
            </div>

            <div>
              <label className={labelClassName}>College / Institute</label>
              <input
                type="text"
                value={formData.academicDetails.graduation?.college || ''}
                onChange={(e) => handleAcademicChange('graduation', 'college', e.target.value)}
                className={inputClassName}
                placeholder="Institute Name"
              />
            </div>

            <div>
              <label className={labelClassName}>University / Board</label>
              <input
                type="text"
                value={formData.academicDetails.graduation?.university || ''}
                onChange={(e) => handleAcademicChange('graduation', 'university', e.target.value)}
                className={inputClassName}
                placeholder="University Name"
              />
            </div>

            <div>
              <label className={labelClassName}>Roll / Registration No.</label>
              <input
                type="text"
                value={formData.academicDetails.graduation?.rollNumber || ''}
                onChange={(e) => handleAcademicChange('graduation', 'rollNumber', e.target.value)}
                className={inputClassName}
                placeholder="University Roll No"
              />
            </div>

            <div>
              <label className={labelClassName}>Passing Year</label>
              <input
                type="number"
                value={formData.academicDetails.graduation?.passingYear || ''}
                onChange={(e) => handleAcademicChange('graduation', 'passingYear', parseInt(e.target.value))}
                className={inputClassName}
                placeholder="2025"
              />
            </div>

            <div>
              <label className={labelClassName}>Percentage / CGPA</label>
              <input
                type="number"
                step="0.01"
                value={formData.academicDetails.graduation?.percentage || ''}
                onChange={(e) => handleAcademicChange('graduation', 'percentage', parseFloat(e.target.value))}
                className={inputClassName}
                placeholder="8.5"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default FormDetailsInputs;